import React, { useId, useRef } from 'react';
import styled from '@emotion/styled';
import type { FlexProps, PositionProps } from 'styled-system';
import { alignItems, display, flex, flexDirection, position, space } from 'styled-system';

import { Direction } from '../../enums/Direction';
import { BodyRegularBR1 } from '../Typography';
import RadioIcon from './RadioIcon';
import RadioInput from './RadioInput';
import type { RadioProps } from './types';

const Label = styled.label<any>`
  ${display};
  ${alignItems};
  ${flex};
  ${space};
  ${flexDirection};

  cursor: pointer;
  color: ${({
    isDisabled,
    theme: {
      colors: { sys },
    },
  }) => (isDisabled ? sys.neutral.text.inactive : sys.neutral.text.default)};

  svg {
    opacity: ${({ isDisabled }) => (isDisabled ? '0.5' : '1')};
  }

  &:hover > svg {
    fill: ${({ selected, isDisabled, theme: { colors } }) =>
      !selected && !isDisabled && colors.sys.neutral.background.hover};
  }
`;

const ltrLabelStyles = {
  flexDirection: 'row',
};

const rtlLabelStlyes = {
  flexDirection: 'row-reverse',
  py: 'sp_8',
};

const LabelText = styled(BodyRegularBR1)<FlexProps>`
  ${flex};
`;

const Container = styled.div<PositionProps>`
  ${position};
`;

export const Radio = ({
  label,
  value,
  direction = Direction.Ltr,
  defaultChecked,
  onChange = () => undefined,
  onKeyDown,
  onBlur,
  name,
  disabled = false,
  iconSize,
  className,
  customIconStyles,
  customLabelStyles,
  ariaLabel,
}: RadioProps) => {
  const labelStyles = direction === Direction.Ltr ? ltrLabelStyles : rtlLabelStlyes;
  const elementRef = useRef<HTMLLabelElement>(undefined);
  const id = useId();

  return (
    <Container position="relative">
      <RadioInput
        data-testid="radio-input"
        id={id}
        name={name}
        value={value}
        onChange={onChange}
        disabled={disabled}
        defaultChecked={defaultChecked}
      />
      <Label
        data-testid="radio-label"
        alignItems="center"
        display="flex"
        flex="1 1 auto"
        mb="sp_16"
        selected={defaultChecked}
        htmlFor={id}
        isDisabled={disabled}
        className={className}
        onKeyDown={onKeyDown}
        onBlur={onBlur}
        ref={elementRef}
        {...labelStyles}
        {...customLabelStyles}
      >
        <RadioIcon
          iconSize={iconSize}
          checked={defaultChecked}
          mr={direction === Direction.Ltr ? 'sp_8' : 'sp_0'}
          {...customIconStyles}
        />
        <LabelText textAlign="left" flex="1 1 auto" color="inherit" aria-label={ariaLabel}>
          {label}
        </LabelText>
      </Label>
    </Container>
  );
};
